import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2"; // ✅ Import SweetAlert2
import { toast } from "react-toastify"; // ✅ Import Toastify
import "react-toastify/dist/ReactToastify.css"; // ✅ Import Toastify CSS
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import BASE_URL from "../config"; // ✅ Import BASE_URL
import Loader from "../components/Loader"; // Import Loader component

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [loading, setLoading] = useState(true); // Add loading state
  const [projectMap, setProjectMap] = useState({}); // Add project map state

  useEffect(() => {
    fetchTeams();
    // Fetch all projects for mapping id -> name
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${BASE_URL}api/project/find-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const map = {};
        (res.data || []).forEach((p) => {
          map[String(p.id)] = p.project_name;
        });
        setProjectMap(map);
      } catch (err) {
        console.error("Error fetching projects for team table:", err);
      }
    };
    fetchProjects();
  }, []);
  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const isAscending = prevConfig.key === key && prevConfig.direction === "asc";
      return {
        key: key,
        direction: isAscending ? "desc" : "asc",
      };
    });
  };

  const sortedTeams = [...teams].sort((a, b) => {
    const aVal = a[sortConfig.key] || "";
    const bVal = b[sortConfig.key] || "";

    // Handle date comparison if the key is 'last_payment_date'
    if (sortConfig.key === "last_payment_date") {
      return sortConfig.direction === "asc"
        ? new Date(aVal) - new Date(bVal)
        : new Date(bVal) - new Date(aVal);
    }

    // Handle string comparison (e.g., team_name)
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortConfig.direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Handle numeric comparison
    return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
  });

  const fetchTeams = () => {
    setLoading(true); // Show loader while fetching data
    const token = localStorage.getItem("token");

    axios
      .get(`${BASE_URL}api/team/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(async (res) => {
        const teamsWithUsernamesAndAvatars = await Promise.all(
          res.data.map(async (team) => {
            const memberIds = team.members
              ? team.members.split(",").map((id) => id.trim())
              : [];

            console.log("Member IDs for team", team.name, memberIds);

            const memberData = await Promise.all(
              memberIds.map(async (id) => {
                try {
                  const response = await axios.get(`${BASE_URL}api/user/find-one/${id}`, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  });

                  console.log(`Fetched user ${id}:`, response.data);

                  // Fetch username and avatar image_url
                  return {
                    username: response.data?.username || "Unknown",
                    image_url: response.data?.image_url || `https://placehold.co/40x40?text=${(response.data?.username?.charAt(0) || "U").toUpperCase()}&font=roboto`,
                  };
                } catch (err) {
                  console.error(`Failed to fetch user ${id}:`, err);
                  return {
                    username: "Unknown",
                    image_url: "https://via.placeholder.com/30",
                  };
                }
              })
            );

            // Fetch the lead's username and image_url
            let leadData = {
              username: "Unknown",
              image_url: "https://via.placeholder.com/30",
            };
            if (team.lead) {
              try {
                const response = await axios.get(`${BASE_URL}api/user/find-one/${team.lead}`, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                });

                leadData = {
                  username: response.data?.username || "Unknown",
                  image_url: response.data?.image_url || "https://via.placeholder.com/30",
                };
              } catch (err) {
                console.error(`Failed to fetch lead ${team.lead}:`, err);
              }
            }

            // Fetch client name based on client ID
            let clientName = "Unknown Client";
            if (team.client) {
              try {
                const response = await axios.get(`${BASE_URL}api/clients/find-one/${team.client}`, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                });

                clientName = response.data?.name || "Unknown Client";
              } catch (err) {
                console.error(`Failed to fetch client ${team.client}:`, err);
              }
            }

            return {
              ...team,
              memberData,
              leadData, // Attach the lead data to the team object
              clientName, // Attach the client name to the team object
            };
          })
        );

        console.log("Teams with usernames, avatars, and client names:", teamsWithUsernamesAndAvatars);

        setTeams(teamsWithUsernamesAndAvatars);
      })
      .catch((err) => console.error("Error fetching teams:", err))
      .finally(() => setLoading(false)); // Hide loader after data is fetched
  };

  const toggleTeamStatus = (team) => {
    const action = team.enabled ? "disable" : "enable";

    Swal.fire({
      title: `Are you sure?`,
      text: `You are about to ${action} "${team.name}" team.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${action} it!`,
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(`${BASE_URL}api/team/enabled/${team.id}`, { enabled: !team.enabled }) // Call enabled API
          .then(() => {
            toast.success(`Team has been ${action}d successfully!`, {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
            setTeams((prevTeams) =>
              prevTeams.map((t) =>
                t.id === team.id ? { ...t, enabled: !team.enabled } : t // Update enabled value
              )
            );
          })
          .catch((error) => {
            console.error("Error toggling team status:", error);
            toast.error("Something went wrong!", {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          });
      }
    });
  };

  const updateTeamStatus = (team, newStatus) => {
    Swal.fire({
      title: `Are you sure?`,
      text: `You are about to change the status of "${team.name}" to "${newStatus}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, change it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(`${BASE_URL}api/team/status/${team.id}`, { status: newStatus }) // Call status API
          .then(() => {
            toast.success(`Status updated to "${newStatus}" successfully!`, {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
            setTeams((prevTeams) =>
              prevTeams.map((t) =>
                t.id === team.id ? { ...t, status: newStatus } : t // Update status value
              )
            );
          })
          .catch((error) => {
            console.error("Error updating team status:", error);
            const errorMessage =
              error.response?.data?.message || "Something went wrong!";
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          });
      }
    });
  };

  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mt-4">
      <h2 className="mb-4 text-center">Teams Dashboard</h2>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <input
          type="text"
          className="form-control w-25"
          placeholder="🔍 Search Teams..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <a href="/create-team" className="btn btn-primary">
          + Add New Team
        </a>
      </div>

      <div className="table-responsive mb-5 pb-5" style={{ overflowX: "auto" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Loader message="Loading teams..." /> {/* Show loader only on the table */}
          </div>
        ) : (
          <table className="table table-bordered table-hover" style={{ minWidth: "1000px" }}>
            <thead>
              <tr style={{ backgroundColor: "red", color: "white" }}>
                <th>#</th>
                <th onClick={() => handleSort("name")}>Team Name</th>
                <th onClick={() => handleSort("name")}>Project</th>
                <th onClick={() => handleSort("name")}>Team Lead</th>
                {/* <th onClick={() => handleSort("name")}>Members</th> */}
                <th>Team Members</th>
                <th onClick={() => handleSort("name")}>Client</th>
                <th onClick={() => handleSort("name")}>Start Date</th>
                <th onClick={() => handleSort("name")}>Deadline</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
            {sortedTeams.map((team, index) => (
                <tr key={team.id || index}>
                  <td>{index + 1}</td>
                  <td>
                    <a href={`/team-details/${team.id}`} className="text-decoration-none fw-bold">
                      {team.name}
                    </a>
                  </td>
                  <td>
                    {/* Show project name instead of project id */}
                    {projectMap[String(team.department)] || "Unknown Project"}
                  </td>
                  <td>
                    <a
                      href={`/user-detail?id=${team.lead}`}
                      className="text-decoration-none"
                    >
                      {team.leadData.username}
                    </a>
                  </td>
                  {/* <td>
                    {team.memberData?.length > 0
                      ? team.memberData.map((member, idx) => (
                          <a
                            key={idx}
                            href={`/user-detail?id=${team.members.split(",")[idx].trim()}`}
                            className="text-decoration-none"
                          >
                            {member.username}
                          </a>
                        ))
                      : "No Members"}
                  </td> */}
                  <td>
                    <div className="avatar-stack position-relative d-flex">
                      {team.memberData?.map((member, idx) => (
                        <img
                          key={idx}
                          src={member.image_url}
                          alt={`Member ${idx}`}
                          className="rounded-circle border border-white me-1"
                          style={{ width: "30px", height: "30px" }}
                        />
                      ))}
                    </div>
                  </td>
                  <td>
                    <a
                      href={`/client-detail?id=${team.client}`}
                      className="text-decoration-none"
                    >
                      {team.clientName}
                    </a>
                  </td>
                  <td>
                    {team.startDate
                      ? new Date(team.startDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </td>
                  <td>
                    {team.deadline
                      ? new Date(team.deadline).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={team.status}
                      onChange={(e) => updateTeamStatus(team, e.target.value)} // Handle status change
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </td>
                  <td className="d-flex gap-2">
                    <a href={`/edit-team?id=${team.id}`} className="btn btn-sm btn-warning">
                      <i className="bi bi-pencil"></i> Edit
                    </a>
                    <button
                      className={`btn btn-sm ${team.enabled ? "btn-secondary" : "btn-success"}`}
                      onClick={() => toggleTeamStatus(team)}
                    >
                      <i className={`bi ${team.enabled ? "bi-ban" : "bi-check-circle"}`}></i>{" "}
                      {team.enabled ? "Disable Team" : "Enable Team"}
                    </button>
                  </td>
                </tr>
              ))}
              {filteredTeams.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center">
                    No teams found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Teams;
